import type { LevelDefinition, MonsterSpawn, MonsterType, RuinPiece } from './LevelTypes';

const wall = (x: number, z: number, width: number, depth: number, rotation = 0, height = 3): RuinPiece =>
  ({ type: 'wall', x, z, width, depth, rotation, height });
const broken = (x: number, z: number, width: number, depth: number, rotation = 0): RuinPiece =>
  ({ type: 'broken-wall', x, z, width, depth, rotation, height: 2.25 });
const column = (x: number, z: number, height = 4.8): RuinPiece => ({ type: 'column', x, z, height });
const arch = (x: number, z: number, rotation = 0): RuinPiece => ({ type: 'arch', x, z, rotation, width: 4.2, depth: 1.0, height: 4.7 });
const tunnel = (x: number, z: number, rotation = 0): RuinPiece => ({ type: 'tunnel', x, z, rotation, width: 5, depth: 8, height: 3.6 });
const rubble = (x: number, z: number, width = 2, depth = 2): RuinPiece => ({ type: 'rubble', x, z, width, depth });
const tree = (x: number, z: number): RuinPiece => ({ type: 'tree', x, z, width: 1.4, depth: 1.4, height: 5 });
const stairs = (x: number, z: number, rotation = 0): RuinPiece => ({ type: 'stairs', x, z, width: 4, depth: 4.8, height: 1.6, rotation });
const platform = (x: number, z: number, width = 6, depth = 6, height = 1): RuinPiece => ({ type: 'platform', x, z, width, depth, height });

function spawn(type: MonsterType, x: number, z: number, yaw = 0, patrol?: MonsterSpawn['patrol']): MonsterSpawn {
  return { type, x, z, yaw, patrol };
}

function courtyardBorder(width: number, depth: number, gaps = true): RuinPiece[] {
  const sideDepth = gaps ? depth * 0.34 : depth;
  const entranceWidth = width * 0.15;
  const entranceX = width * 0.425;
  return [
    wall(0, -depth / 2, width, 1.3),
    wall(-entranceX, depth / 2, entranceWidth, 1.3), wall(entranceX, depth / 2, entranceWidth, 1.3),
    wall(-width / 2, -depth * 0.31, 1.3, sideDepth), wall(-width / 2, depth * 0.31, 1.3, sideDepth),
    wall(width / 2, -depth * 0.31, 1.3, sideDepth), wall(width / 2, depth * 0.31, 1.3, sideDepth),
  ];
}

const level1Monsters: MonsterSpawn[] = [
  spawn('stalker', -13, -9, 0.4), spawn('stalker', -6, -11, -0.2), spawn('stalker', 2, -10, 0),
  spawn('stalker', 10, -9, -0.5), spawn('stalker', 14, -2, -1.2), spawn('stalker', 11, 7, -2.4),
  spawn('stalker', 0, 0, 0), spawn('stalker', -11, 8, 2.2), spawn('stalker', 13, 10, -2.2),
  spawn('stalker', -14, 1, 1.4), spawn('patrol', 2, -6, 0, [{ x: -3, z: -6 }, { x: 5, z: -6 }]),
  spawn('heavy', 7, -2, Math.PI),
];

export const LEVELS: LevelDefinition[] = [
  {
    id: 1, name: 'Sunwake Courtyard', subtitle: 'The mouths are mighty. Their backs are not.',
    hint: 'Circle pillars to reach the safe side.', width: 38, depth: 32, playerSpawn: { x: 0, z: 8 },
    pieces: [...courtyardBorder(38, 32), column(-9, -3), column(9, -3), column(-9, 6), column(9, 6),
      arch(0, -14.7), rubble(-15, 12), rubble(15, 12), tree(-16, -12), tree(16, -11)],
    monsters: level1Monsters, fogDensity: 0.008, timeOfDay: 'sunrise',
  },
  {
    id: 2, name: 'Hall of Turning', subtitle: 'Stone columns make excellent dance partners.',
    hint: 'Break sightlines, then change direction.', width: 40, depth: 34, playerSpawn: { x: 0, z: 14 },
    pieces: [...courtyardBorder(40, 34), ...[-12, -4, 4, 12].flatMap((x) => [column(x, -6), column(x, 5)]),
      broken(-8, 0, 5, 1.2), broken(8, 0, 5, 1.2), arch(0, -15.5), rubble(-17, 11), rubble(17, -10)],
    monsters: [
      spawn('stalker', -15, -11), spawn('stalker', -7, -12), spawn('stalker', 1, -11), spawn('stalker', 10, -12),
      spawn('patrol', 15, -5, -1.4, [{ x: 15, z: -8 }, { x: 15, z: 8 }]), spawn('stalker', 14, 9, 2.5),
      spawn('stalker', 7, 11, 3), spawn('patrol', -2, 7, 3, [{ x: -7, z: 7 }, { x: 6, z: 7 }]),
      spawn('stalker', -10, 10, 2.5), spawn('heavy', -15, 2, 1.2), spawn('heavy', 6, -1, -2.2), spawn('stalker', -2, -3),
    ], fogDensity: 0.009, timeOfDay: 'day',
  },
  {
    id: 3, name: 'The Split Garden', subtitle: 'Every broken road hides another route.',
    hint: 'Use the multiple paths to approach from behind.', width: 42, depth: 36, playerSpawn: { x: -10, z: 14 },
    pieces: [...courtyardBorder(42, 36), wall(0, -9, 1.4, 12), wall(0, 9, 1.4, 10), broken(-9, 1, 10, 1.3),
      broken(10, -1, 9, 1.3), arch(0, 0, Math.PI / 2), column(-15, -10), column(15, 10), tree(-17, 4), tree(17, -5),
      rubble(-7, -13), rubble(8, 13)],
    monsters: [
      spawn('stalker', -16, -12), spawn('stalker', -9, -9), spawn('chaser', -7, 6), spawn('patrol', -16, 1, 0, [{ x: -16, z: -7 }, { x: -16, z: 9 }]),
      spawn('stalker', -8, 13), spawn('heavy', -2, -5), spawn('stalker', 5, -13), spawn('chaser', 13, -12),
      spawn('patrol', 9, -4, 0, [{ x: 5, z: -4 }, { x: 16, z: -4 }]), spawn('stalker', 15, 4), spawn('heavy', 8, 8), spawn('stalker', 16, 13),
    ], fogDensity: 0.011, timeOfDay: 'sunset',
  },
  {
    id: 4, name: 'Whispering Passages', subtitle: 'Less room. More nerve.',
    hint: 'Let a monster commit before slipping past.', width: 42, depth: 38, playerSpawn: { x: -11, z: 16 },
    pieces: [...courtyardBorder(42, 38), wall(-10, -7, 1.3, 20), wall(0, 7, 1.3, 20), wall(10, -7, 1.3, 20),
      broken(-5, 0, 9, 1.2), broken(5, -1, 9, 1.2), column(-16, 0), column(16, 0), rubble(4, 15)],
    monsters: [
      spawn('stalker', -16, -13), spawn('chaser', -5, -15), spawn('small', 5, -15), spawn('stalker', 16, -14),
      spawn('patrol', -15, -2, 0, [{ x: -15, z: -8 }, { x: -15, z: 8 }]), spawn('stalker', -5, -3),
      spawn('small', 5, -5), spawn('heavy', 15, -2), spawn('stalker', -15, 10), spawn('chaser', -5, 12),
      spawn('stalker', 5, 8), spawn('small', 15, 11), spawn('patrol', 2, 16, 0, [{ x: -7, z: 16 }, { x: 10, z: 16 }]), spawn('heavy', 10, 2),
    ], fogDensity: 0.013, timeOfDay: 'twilight',
  },
  {
    id: 5, name: 'Peekaboo Cloister', subtitle: 'Some ruins are looking back.',
    hint: 'Ambushers wait near doorways; listen for their pop!', width: 44, depth: 38, playerSpawn: { x: 0, z: 16 },
    pieces: [...courtyardBorder(44, 38), arch(-10, 4, Math.PI / 2), arch(10, 4, Math.PI / 2), wall(-10, -7, 1.3, 15),
      wall(10, -7, 1.3, 15), broken(0, -7, 10, 1.2), column(-17, 10), column(17, 10), tree(-18, -14), tree(18, -14), rubble(0, 10)],
    monsters: [
      spawn('ambusher', -12, 3, 1.3), spawn('ambusher', 12, 3, -1.3), spawn('ambusher', -8, -13), spawn('ambusher', 8, -13),
      spawn('stalker', -18, -8), spawn('chaser', 18, -8), spawn('small', -17, 12), spawn('stalker', 17, 12),
      spawn('patrol', 0, -15, 0, [{ x: -7, z: -15 }, { x: 7, z: -15 }]), spawn('heavy', 0, -2),
      spawn('stalker', -5, 9), spawn('chaser', 6, 10), spawn('small', -17, -1), spawn('heavy', 17, -1), spawn('ambusher', 0, -9),
    ], fogDensity: 0.012, timeOfDay: 'sunset',
  },
  {
    id: 6, name: 'Three Sun Courts', subtitle: 'One ruin, three little battlefields.',
    hint: 'Clear space methodically and keep an escape corridor.', width: 48, depth: 42, playerSpawn: { x: -13, z: 18 },
    pieces: [...courtyardBorder(48, 42), wall(-8, -11, 1.4, 14), wall(-8, 12, 1.4, 14), wall(8, -11, 1.4, 14), wall(8, 12, 1.4, 14),
      arch(-8, 0, Math.PI / 2), arch(8, 0, Math.PI / 2), column(-18, -10), column(18, 10), tree(0, 0), rubble(-18, 10), rubble(18, -10)],
    monsters: [
      spawn('stalker', -19, -16), spawn('small', -13, -10), spawn('curious', -18, 0), spawn('heavy', -15, 11), spawn('ambusher', -10, 2),
      spawn('chaser', -2, -16), spawn('patrol', 0, -8, 0, [{ x: -4, z: -8 }, { x: 5, z: -8 }]), spawn('stalker', 1, 8), spawn('small', 0, 17), spawn('ambusher', 6, 2),
      spawn('heavy', 15, -15), spawn('curious', 19, -6), spawn('chaser', 14, 2), spawn('stalker', 19, 12), spawn('small', 12, 16), spawn('patrol', 18, 5, 0, [{ x: 12, z: 5 }, { x: 20, z: 5 }]),
    ], fogDensity: 0.014, timeOfDay: 'day',
  },
  {
    id: 7, name: 'Steps of the Sky', subtitle: 'High roads, low tunnels, quick decisions.',
    hint: 'Stairs and platforms reshape sightlines.', width: 48, depth: 44, playerSpawn: { x: 0, z: 19 },
    pieces: [...courtyardBorder(48, 44), platform(-13, -10, 9, 9, 1.2), stairs(-13, -3, Math.PI), platform(13, 8, 9, 9, 1.2), stairs(13, 1),
      wall(0, -10, 1.4, 20), tunnel(0, 5, Math.PI / 2), broken(-8, 10, 8, 1.2), broken(8, -15, 8, 1.2), column(-19, 8), column(19, -8), tree(-20, -17)],
    monsters: [
      spawn('curious', -19, -17), spawn('chaser', -9, -17), spawn('small', 4, -18), spawn('stalker', 18, -17),
      spawn('heavy', -13, -9), spawn('ambusher', -6, -3), spawn('patrol', 8, -8, 0, [{ x: 5, z: -8 }, { x: 19, z: -8 }]),
      spawn('small', 13, 8), spawn('stalker', 19, 7), spawn('curious', -19, 3), spawn('chaser', -10, 8),
      spawn('ambusher', 2, 6), spawn('heavy', 8, 15), spawn('small', 19, 17), spawn('patrol', -8, 17, 0, [{ x: -17, z: 17 }, { x: 2, z: 17 }]),
      spawn('stalker', 2, -2), spawn('ambusher', 16, 1),
    ], fogDensity: 0.016, timeOfDay: 'sunrise',
  },
  {
    id: 8, name: 'Secretleaf Maze', subtitle: 'The quietest alcove may have tiny feet.',
    hint: 'Watch corners and use curious monsters against each other.', width: 50, depth: 44, playerSpawn: { x: -14, z: 19 },
    pieces: [...courtyardBorder(50, 44), wall(-14, -8, 1.3, 21), wall(-4, 9, 1.3, 21), wall(6, -8, 1.3, 21), wall(16, 9, 1.3, 21),
      broken(-9, 0, 9, 1.2), broken(11, 0, 9, 1.2), ...[-19, -9, 1, 11, 21].map((x) => tree(x, -18 + (x % 3) * 2)),
      arch(-4, -3, Math.PI / 2), arch(16, 3, Math.PI / 2), rubble(-20, 10), rubble(20, -10)],
    monsters: [
      spawn('ambusher', -17, -15), spawn('small', -8, -17), spawn('curious', 1, -18), spawn('ambusher', 10, -15), spawn('chaser', 21, -17),
      spawn('patrol', -20, -3, 0, [{ x: -20, z: -10 }, { x: -20, z: 10 }]), spawn('heavy', -10, -4), spawn('small', 1, -6),
      spawn('curious', 11, -3), spawn('ambusher', 20, -4), spawn('stalker', -19, 13), spawn('chaser', -10, 15),
      spawn('ambusher', 0, 10), spawn('small', 10, 16), spawn('heavy', 21, 13), spawn('patrol', 5, 19, 0, [{ x: -2, z: 19 }, { x: 15, z: 19 }]),
      spawn('curious', -7, 5), spawn('stalker', 15, 6),
    ], fogDensity: 0.018, timeOfDay: 'twilight',
  },
  {
    id: 9, name: 'The Many-Mouth Forum', subtitle: 'Every trick returns at once.',
    hint: 'Choose your target; a rushed kick is an open invitation.', width: 52, depth: 46, playerSpawn: { x: 0, z: 20 },
    pieces: [...courtyardBorder(52, 46), column(-16, -14), column(-6, -14), column(6, -14), column(16, -14),
      wall(-13, -3, 12, 1.3), wall(13, -3, 12, 1.3), broken(0, 6, 13, 1.3), arch(0, -4),
      tree(-21, 11), tree(21, 11), platform(-16, 12, 7, 7, 0.8), stairs(-16, 7, Math.PI), rubble(16, 14)],
    monsters: [
      spawn('heavy', -20, -18), spawn('small', -10, -19), spawn('chaser', 0, -18), spawn('curious', 10, -19), spawn('ambusher', 20, -18),
      spawn('patrol', -20, -8, 0, [{ x: -20, z: -12 }, { x: -20, z: 5 }]), spawn('stalker', -8, -8), spawn('small', 8, -9), spawn('heavy', 20, -7),
      spawn('ambusher', -14, 1), spawn('curious', 0, 1), spawn('chaser', 14, 1), spawn('small', -21, 10),
      spawn('heavy', -10, 13), spawn('ambusher', 1, 10), spawn('patrol', 11, 13, 0, [{ x: 6, z: 13 }, { x: 19, z: 13 }]),
      spawn('chaser', 21, 17), spawn('stalker', -2, 19), spawn('curious', -16, 17),
    ], fogDensity: 0.016, timeOfDay: 'sunset',
  },
  {
    id: 10, name: 'Temple of the Last Laugh', subtitle: 'The whole ruin has taught you this dance.',
    hint: 'Bait, break sight, flank, kick. You know the rhythm.', width: 54, depth: 48, playerSpawn: { x: -15, z: 21 },
    pieces: [...courtyardBorder(54, 48), arch(0, -21.8), arch(-13, 0, Math.PI / 2), arch(13, 0, Math.PI / 2),
      wall(-13, -12, 1.4, 13), wall(-13, 12, 1.4, 13), wall(13, -12, 1.4, 13), wall(13, 12, 1.4, 13),
      platform(0, 0, 9, 9, 1.25), stairs(0, 7), stairs(0, -7, Math.PI),
      column(-21, -17), column(21, -17), column(-21, 17), column(21, 17), tree(-23, 0), tree(23, 0),
      broken(-6, -16, 8, 1.2), broken(7, 16, 8, 1.2), rubble(-7, 8), rubble(8, -8)],
    monsters: [
      spawn('ambusher', -21, -19), spawn('small', -11, -20), spawn('chaser', 0, -20), spawn('curious', 11, -20), spawn('heavy', 21, -19),
      spawn('patrol', -20, -8, 0, [{ x: -20, z: -13 }, { x: -20, z: 8 }]), spawn('stalker', -7, -11), spawn('small', 7, -11), spawn('ambusher', 20, -8),
      spawn('curious', -7, 0), spawn('heavy', 0, 0), spawn('chaser', 7, 0), spawn('ambusher', -20, 8), spawn('small', -7, 10),
      spawn('stalker', 7, 11), spawn('patrol', 20, 8, 0, [{ x: 20, z: -1 }, { x: 20, z: 15 }]),
      spawn('heavy', -20, 18), spawn('curious', -10, 20), spawn('chaser', 10, 20), spawn('ambusher', 21, 19),
    ], fogDensity: 0.019, timeOfDay: 'sunrise',
  },
];

export function getLevel(id: number): LevelDefinition {
  return LEVELS[Math.max(0, Math.min(LEVELS.length - 1, id - 1))]!;
}
